import I18n from 'i18n-js';
import React from 'react';
import classNames from 'classnames';
import { withRouter, WithRouterProps } from 'react-router';
import track from 'react-tracking';

import CoreLink from './core_link';
import { MParticleEventName } from '../elog/mparticle_types';

const LEFT_GAP = 'previous_ellipsis';
const RIGHT_GAP = 'next_ellipsis';

const range = function range(from, to) {
  const array = [];

  for (let i = from; i <= to; i += 1) {
    array.push(i);
  }

  return array;
};

interface IProps extends WithRouterProps {
  limit: number;
  offset: number;
  total: number;
  paginationWindow?: number;
  handlePaginationClick?: Function;
  anchor?: string;
  pageParam?: string;
  replaceHistory?: boolean;
  additionalParam?: string;
  additionalParamValue?: string;
  trackInMParticle?: boolean;
  experimentValue?: string;
}

const COMPONENT_NAME = 'RQLPagination';

@track({ componentName: COMPONENT_NAME })
export class RQLPagination extends React.Component<IProps, null> {
  static defaultProps = {
    limit: 0,
    offset: 0,
    total: 0,
    paginationWindow: 2,
    pageParam: 'page',
    replaceHistory: false,
  };

  get currentPage() {
    const page = Math.ceil(this.props.offset / this.props.limit) + 1;

    return page < 1 ? 1 : page;
  }

  get totalPages() {
    return Math.ceil(this.props.total / this.props.limit);
  }

  validPage(page) {
    return page !== this.currentPage;
  }

  get middleRange() {
    let windowFrom = this.currentPage - this.props.paginationWindow;
    let windowTo = this.currentPage + this.props.paginationWindow;

    const { totalPages } = this;

    if (windowTo > totalPages) {
      windowFrom -= windowTo - totalPages;
      windowTo = totalPages;
    }

    if (windowFrom < 1) {
      windowTo += 1 - windowFrom;
    }

    return range(
      Math.max(1, windowFrom + 1),
      Math.min(windowTo, totalPages),
    );
  }

  get leftRange() {
    if (this.props.paginationWindow < this.middleRange[0]) {
      return [1, LEFT_GAP]; // display first page and gap only
    }

    // display all pages from beginning to middle range
    return range(1, this.middleRange[0] - 1);
  }

  get rightRange() {
    const { totalPages } = this;
    const endOfMiddleRange = this.middleRange[this.middleRange.length - 1];

    if (totalPages > endOfMiddleRange) {
      return [RIGHT_GAP]; // display gap only
    }

    // display all pages from middle range to end
    return range(endOfMiddleRange + 1, totalPages);
  }

  get pageNumbers() {
    return this.leftRange.concat(this.middleRange).concat(this.rightRange);
  }

  getMParticleData(pageNum: number) {
    if (!this.props.trackInMParticle) { return null; }

    return {
      componentName: COMPONENT_NAME,
      position: pageNum,
      eventName: MParticleEventName.ClickedPagination,
      experiments: this.props.experimentValue,
    };
  }

  renderEllipsisLink(pageNum) {
    const key = `pagination-gap-${pageNum}`;
    const classes = classNames(
      'pagination__page',
      'pagination__page--gap',
      { 'pagination__page--before': pageNum < this.currentPage },
      { 'pagination__page--after': pageNum > this.currentPage },
    );

    return (
      <li
        className={classes}
        key={key}
      >
        <span className="pagination__page__ellipsis" aria-hidden="true" />
        <span className="pagination__page__gap-label">
          {I18n.t('commons.paginationGap')}
        </span>
      </li>
    );
  }

  renderPageNumberLink(pageNum) {
    const classes = classNames(
      'pagination__page',
      'pagination__page--number',
      { 'pagination__page--active': pageNum === this.currentPage },
      { 'pagination__page--before': pageNum < this.currentPage },
      { 'pagination__page--after': pageNum > this.currentPage },
    );

    return (
      <li key={pageNum} className={classes}>
        <CoreLink
          to={{
            query: {
              ...this.props?.location?.query,
              [this.props.pageParam]: pageNum,
              [this.props.additionalParam]: this.props.additionalParamValue,
            },
            anchor: this.props.anchor,
          }}
          clickEvent={this.getMParticleData(pageNum)}
          rel="nofollow"
          replace={this.props.replaceHistory}
        >
          {pageNum}
        </CoreLink>
      </li>
    );
  }

  renderPreviousButton() {
    if (this.currentPage === 1) { return null; }

    const newPage = this.currentPage - 1;
    return (
      <li key="previous" className="pagination__page pagination__page--previous">
        <CoreLink
          to={{
            query: {
              ...this.props?.location?.query,
              [this.props.pageParam]: newPage,
            },
            anchor: this.props.anchor,
          }}
          rel="nofollow"
          replace={this.props.replaceHistory}
        >
          {I18n.t('commons.previous')}
        </CoreLink>
      </li>
    );
  }

  renderNextButton() {
    if (this.currentPage === this.totalPages) { return null; }

    const newPage = this.currentPage + 1;
    return (
      <li key="next" className="pagination__page pagination__page--next">
        <CoreLink
          to={{
            query: {
              ...this.props?.location?.query,
              [this.props.pageParam]: newPage,
            },
            anchor: this.props.anchor,
          }}
          replace={this.props.replaceHistory}
          rel="nofollow"
        >
          {I18n.t('commons.next')}
        </CoreLink>
      </li>
    );
  }

  renderPages() {
    return this.pageNumbers.map((pageNum) => {
      if (pageNum === LEFT_GAP || pageNum === RIGHT_GAP) {
        return this.renderEllipsisLink(pageNum);
      }
      return this.renderPageNumberLink(pageNum);
    });
  }

  render() {
    if (this.totalPages <= 1 || (this.props.offset >= this.props.total)) { return null; }

    return (
      <div className="pagination-container">
        <ul>
          {this.renderPreviousButton()}
          {this.renderPages()}
          {this.renderNextButton()}
        </ul>
      </div>
    );
  }
}

export default withRouter(RQLPagination);
